/**
 * Visibility Configuration Module
 * 
 * Centralizes time-based visibility control for sections of the hackathon event SPA.
 * Sections can have optional visibility thresholds - if no threshold is defined,
 * the section is always visible.
 */

export interface SectionVisibilityConfig {
  id: string;
  visibleAfter?: Date; // undefined means always visible
}

export const VISIBILITY_CONFIG: SectionVisibilityConfig[] = [
  { id: 'live' }, // Always visible
  { id: 'info', visibleAfter: new Date('2026-02-17T07:30:00Z') }, // 8:30 German time (CET = UTC+1)
  { id: 'agenda' }, // Always visible
  { id: 'forge', visibleAfter: new Date('2026-02-17T07:30:00Z') }, // 8:30 German time (CET = UTC+1)
  { id: 'quests', visibleAfter: new Date('2026-02-17T07:30:00Z') }, // 8:30 German time (CET = UTC+1)
];

/**
 * Checks if a specific section is visible at the given time.
 * 
 * @param sectionId - The ID of the section to check
 * @param currentTime - The time to check against (defaults to current time)
 * @returns true if the section is visible, false otherwise
 */
export function isSectionVisible(
  sectionId: string,
  currentTime: Date = new Date()
): boolean {
  const config = VISIBILITY_CONFIG.find(c => c.id === sectionId);
  if (!config) return true; // Unknown sections are visible by default
  if (!config.visibleAfter) return true; // No threshold means always visible
  return currentTime >= config.visibleAfter;
}

/**
 * Gets all sections that are visible at the given time.
 * 
 * @param currentTime - The time to check against (defaults to current time)
 * @returns Array of section IDs that are currently visible
 */
export function getVisibleSections(currentTime: Date = new Date()): string[] {
  return VISIBILITY_CONFIG
    .filter(config => isSectionVisible(config.id, currentTime))
    .map(config => config.id);
}

/**
 * Finds the next upcoming visibility threshold after the given time.
 * 
 * @param currentTime - The time to check from (defaults to current time)
 * @returns The next threshold Date, or null if no future thresholds exist
 */
export function findNextThreshold(currentTime: Date = new Date()): Date | null {
  const futureThresholds = VISIBILITY_CONFIG
    .filter(config => config.visibleAfter && config.visibleAfter > currentTime)
    .map(config => config.visibleAfter!)
    .sort((a, b) => a.getTime() - b.getTime());

  return futureThresholds.length > 0 ? futureThresholds[0]! : null;
}
